import React, { useState } from 'react';
import { 
  Moon, Menu, X, Clock, Shield, Baby, Heart, Star, 
  BedDouble, Sparkles, CloudMoon, Zap, Stethoscope, Users, HeartHandshake
} from 'lucide-react';
import NavBar from './NavBar'; // Import du composant NavBar
import nurseNuitImage1 from './nurse-nuit-image1.jpg'; // Première image à ajouter
import nurseNuitImage2 from './nurse-nuit-image2.jpg'; // Deuxième image à ajouter

const NurseNuitPage = () => {
  const [activeSection, setActiveSection] = useState(null);

  const services = [
    "La présence rassurante d'une nurse de nuit toute la nuit",
    "La prise en charge complète des soins nocturnes (biberons, changes, bercements, réassurance)",
    "Le respect du rythme de sommeil de votre bébé",
    "Un soutien précieux lors du retour à la maison après l'accouchement, ou en cas de fatigue parentale"
  ];

  const expertise = [
    {
      title: "Expertise en soins du nourrisson",
      description: "Une solide expertise professionnelle pour prendre soin de votre bébé",
      icon: Baby
    },
    {
      title: "Écoute active et bienveillante",
      description: "Une présence rassurante qui comprend vos besoins",
      icon: Heart
    },
    {
      title: "Adaptation personnalisée",
      description: "Une grande capacité d'adaptation aux besoins de votre bébé et de votre foyer",
      icon: Users
    }
  ];

  const benefits = [
    {
      title: "Préserver votre santé",
      description: "Maintenir votre santé mentale et physique",
      icon: Shield,
      color: "text-blue-600"
    },
    {
      title: "Récupération post-partum",
      description: "Favoriser une récupération optimale après l'accouchement",
      icon: Heart,
      color: "text-rose-600"
    },
    {
      title: "Éviter l'épuisement",
      description: "Prévenir l'épuisement parental",
      icon: Sparkles,
      color: "text-purple-600"
    },
    {
      title: "Soutien professionnel",
      description: "Bénéficier d'un accompagnement rassurant",
      icon: HeartHandshake,
      color: "text-amber-600"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation avec NavBar */}
      <NavBar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 to-blue-700 overflow-hidden">
        <div className="absolute inset-0">
          <div className="stars"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-16 md:py-24">
          <Moon className="w-20 h-20 text-amber-300 mx-auto mb-6 animate-pulse" />
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
            Nurse de Nuit
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 font-medium mb-4">
            Garde de nuit pour bébé à domicile
          </p>
          
        </div>
      </div>

      {/* Introduction avec première image */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/2 p-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-8">🌙 Un accompagnement bienveillant pour des nuits plus sereines</h2>
              <p className="text-slate-600 mb-6 text-lg">
                Vous êtes de jeunes parents épuisés ? Vous avez besoin de repos après l'accouchement ? <strong>Nurse de Nuit</strong> vous propose un service professionnel de <strong>garde de nuit pour bébé à domicile</strong>, assuré par des <strong>professionnelles expérimentées</strong> et formées en <strong>périnatalité</strong>.
              </p>
              <p className="text-slate-700 text-lg font-medium">
                Notre mission : vous offrir un <strong className="text-blue-600">accompagnement de nuit sur mesure</strong>, dans la bienveillance et la discrétion, pour que vous puissiez <strong className="text-blue-600">retrouver des nuits paisibles</strong>.
              </p>
            </div>
            <div className="md:w-1/2 relative h-64 md:h-auto">
              <img 
                src={nurseNuitImage1} 
                alt="Nurse de nuit avec bébé" 
                className="w-full h-full object-cover absolute inset-0"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Services de Nuit */}
      <div className="bg-blue-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
            Nos services – Garde de nuit personnalisée pour bébé
          </h2>
          <p className="text-center text-slate-600 mb-8 max-w-3xl mx-auto text-lg">
            Nous intervenons à domicile dans toute la région Paris, Île de France pour prendre soin de votre nouveau-né ou enfant pendant la nuit, et ce dès la sortie de la maternité.
          </p>
          
          <div className="bg-white rounded-lg shadow-md p-8 max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-slate-800 mb-6">Nos prestations incluent :</h3>
            <ul className="space-y-4">
              {services.map((service, index) => (
                <li key={index} className="flex items-start">
                  <Moon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">{service}</span>
                </li>
              ))}
            </ul>
            
            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <p className="text-slate-700">
                Ce service est idéal pour les <strong>mamans en post-partum</strong>, les <strong>parents isolés</strong>, ou les familles qui traversent une période difficile. Nous proposons des <strong>gardes ponctuelles</strong> ou un <strong>accompagnement régulier</strong>, selon vos besoins.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Notre approche avec deuxième image */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-4">
          Notre approche – Expertise, douceur et respect
        </h2>
        <p className="text-center text-slate-600 mb-12 max-w-3xl mx-auto text-lg">
          Chaque famille est unique. C'est pourquoi nous adoptons une approche <strong>sur mesure, non jugeante et respectueuse de vos choix parentaux</strong>.
        </p>
        
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1">
            <img 
              src={nurseNuitImage2} 
              alt="Professionnelle avec nouveau-né" 
              className="w-full h-full object-cover rounded-lg shadow-lg"
              style={{ maxHeight: '400px' }}
            />
          </div>
          
          <div className="order-1 md:order-2">
            <p className="text-slate-600 mb-6">
              Nos professionnelles sont des intervenantes qualifiées (infirmières puéricultrices, auxiliaires de puériculture…) qui allient :
            </p>
            
            <div className="space-y-6">
              {expertise.map((item, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <item.icon className="w-10 h-10 text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-slate-800 mb-1">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-400">
              <p className="text-slate-700">
                Nous nous appuyons sur les <strong>recommandations pédiatriques</strong> et notre expérience pour vous accompagner avec professionnalisme et empathie.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pourquoi faire appel à une nurse de nuit */}
      <div className="bg-gradient-to-br from-blue-100 to-blue-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8">
            Pourquoi faire appel à une nurse de nuit ?
          </h2>
          
          <p className="text-center text-lg text-slate-700 mb-12 max-w-3xl mx-auto">
            Faire appel à une <strong>nurse de nuit à domicile</strong>, c'est :
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                <benefit.icon className={`w-12 h-12 ${benefit.color} mx-auto mb-4`} />
                <h3 className="text-lg font-bold text-slate-800 mb-2">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Offrez-vous des Nuits Sereines
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Nos nurses de nuit professionnelles veillent sur votre bébé avec douceur et expertise
          </p>
          <a 
            href="mailto:etoileveilleuse@gmail.com"
            className="inline-block bg-amber-400 text-slate-800 px-8 py-4 rounded-full font-semibold hover:bg-amber-500 transform transition hover:scale-105"
          >
            Réserver une Nurse de Nuit
          </a>
        </div>
      </div>

      {/* Styles pour les étoiles */}
      <style jsx>{`
        .stars {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: 
            radial-gradient(2px 2px at 20px 30px, white, transparent),
            radial-gradient(2px 2px at 40px 70px, white, transparent),
            radial-gradient(1px 1px at 50px 160px, white, transparent),
            radial-gradient(1px 1px at 130px 40px, white, transparent),
            radial-gradient(2px 2px at 80px 10px, white, transparent);
          background-repeat: repeat;
          background-size: 200px 200px;
          animation: twinkle 5s infinite;
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default NurseNuitPage;